/* eslint-disable react-hooks/exhaustive-deps */
import React, { Dispatch, ForwardedRef, HTMLAttributes, forwardRef, useImperativeHandle, useRef } from 'react'
import { FC, useCallback, useEffect, useState } from 'react'
import { Waypoint } from 'react-waypoint'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Button, Checkbox, Chip, CircularProgress, Tooltip, useTheme } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { cloneDeep, debounce, find, isEqual } from 'lodash'

import { useField, useFormikContext } from 'formik'

import { ELEMENT_RADIUS_MID } from 'utils/customizedTheme'
import { FaInfoCircle } from 'react-icons/fa'
import InfoTooltip from '../buttons/InfoTooltip'
import { MAX_PER_PAGE } from 'utils/constants'

interface IProps {
  fieldProps: TextFieldProps
  apiCall: any
  onChange?: any
  onReload?: any
  disabled?: boolean
  disableClearable?: boolean
  multiple?: boolean
  limitTags?: number
  valueSelector?: string
  renderChildren?: boolean
  filters?: any
  selectFirst?: boolean
  hasHint?: boolean
  hint?: string
}

interface IValue {
  id: number
  title: string
  hidden?: boolean
  hint?: string
  parents?: IValue[]
}

const SelectField: FC<IProps> = ({
  fieldProps,
  apiCall,
  onChange,
  onReload,
  disabled,
  disableClearable,
  multiple,
  limitTags,
  valueSelector,
  renderChildren = false,
  filters = {},
  selectFirst = false,
  hasHint = false,
  hint
}) => {
  const theme = useTheme()

  const [field, meta] = useField(fieldProps.name) // Formik props to handle da form data
  const { setFieldValue } = useFormikContext() // Manually set field value in Formik state
  const { touched, error } = meta // Formik indicators if the field is touched or has validation errors from Yup
  const renderHelperText = () => {
    // Error messages component
    if (touched && error) {
      return error
    }
  }

  // Fetch data parameters
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  // Fetch data/options for the DDL
  const { isFetching, data, error: fetchError, refetch } = apiCall({ page, page_size: MAX_PER_PAGE, ...filters }, {
    refetchOnMountOrArgChange: true
  })
  // const { isFetching, data, error: fetchError } = apiCall(filters)
  const [hasInitialValue, setHasInitialValue] = useState(false)
  const [hasFirstValue, setHasFirstValue] = useState(false)

  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<IValue[]>([])

  const [value, setValue] = useState<any>(multiple ? [] : null)


  // Set initial values
  useEffect(() => {

    if (field?.value?.id && !hasInitialValue && data?.list.length) {
      
      let initialOptions: IValue[] = cloneDeep(data.list)

      if (!initialOptions.some((opt) => opt.id === field.value.id)) {
        initialOptions.unshift(field.value) // If the initial option is not on the first page will be added it in the options list
      }
      setOptions(initialOptions)

      if (field?.value?.parents) {
        const title = generateTitle(field?.value?.title, field?.value) // For nested option
        setInputValue(title)
      } else {
        const title = field?.value?.title || '' // For classic options
        setInputValue(title)
      }

      setHasInitialValue(true) // Flag to prevent this logic to be executed more the once on initialization

    }
  }, [field?.name, field?.value, data?.list, data?.hasMore, hasInitialValue])



  // On refetch
  useEffect(() => {
    if (onReload && data?.list) {
      onReload(data.list) 
    }
  }, [data?.list])


  // Set first value
  useEffect(() => {
    if (selectFirst && data?.list?.length) {

      setFieldValue(field.name, data.list[0])
      if (onChange) onChange(data.list[0]) 
    }
  }, [data?.list?.length])
  

  // Reset page on filters change
  useEffect(() => {
    if(Object.keys(filters).length > 0){
      setPage(() => 1)
      //refetch()
    }
  }, [filters])

  

  /*
  useEffect(() => {
    console.log(1)
    setPage(() => 1)
  }, [filters])
  */

  // Update options on page change
  useEffect(() => {
    if (data?.list?.length > 0) {
      if (page === 1) {
        if (!isEqual(options, data.list)) setOptions(data.list)
      } else {
        if (find(options, data.list)) {
          setOptions([...options, ...data.list.filter((option: IValue) => option?.id !== field?.value?.id)])
        }
      }
    } else {
      if (options?.length > 0) setOptions([])
    }
  }, [page, isFetching])

  // Use FE ot BE search & filter  based on if all date is present or not
  const [hasMore, setHasMore] = useState(false)

  useEffect(() => {
    setHasMore((prevState) => {
      if (prevState) {
        return prevState
      } else {
        return data?.hasMore
      }
    })
  }, [data?.hasMore])

  let filterOptions = {}
  if (hasMore) filterOptions = { filterOptions: (options: any) => options }

  // Wait for the user stop typing before sending the request
  const setFilter = useCallback(
    debounce((value) => {
      setPage(() => 1)
      setSearch(value)
    }, 500),
    [],
  )

  

  // Used to generate title for the nested fields
  const generateTitle = (title: string, option: IValue) => {
    let generatedTitle = title
    generatedTitle = option.parents.map((p) => p.title).join(' > ')
    generatedTitle += option?.parents.length ? ` > ${option.title}` : option.title

    return generatedTitle
  }


  useEffect(() => {
    setValue(valueSelector ? field.value[valueSelector] : field.value)
  }, [field?.value, valueSelector])



  //let value = valueSelector ? field.value[valueSelector] : field.value
  //console.log(value)

  return (
    <div style={{ position: 'relative', width: '100%' }}>
    <Autocomplete
      size='small'
      limitTags={limitTags}
      multiple={multiple} // Multiple options selection allowed
      disableCloseOnSelect={multiple} // Multiple options selection allowed
      ListboxComponent={DropDownList} // Prevents scroll to top on next page
      value={value} // Handles selected option
      onChange={(event, newValue: IValue | IValue[]) => {
        if (valueSelector) {
          newValue = {
            ...field.value,
            [valueSelector]: newValue,
          }
        }
        // Handles selected option change
        setFieldValue(field.name, newValue) // Set selected value in formik state
        if (onChange) onChange(newValue || '') // If extra action is needed on change
      }}
      inputValue={inputValue} // Handles value in the search field
      onOpen={(event)=>{
        if(!multiple){
          setValue(null)
        } 
          
      }}
      onInputChange={(event, newInputValue, reason) => {
        // Handles search field change
        // Use FE or BE search & filter

        if (event && event.type === 'blur') {
          if (hasMore) setFilter(newInputValue)
        } else if (reason !== 'reset') {
          if (hasMore) setFilter(newInputValue)
        }

        const newOptions = options.map(o => {
            return {
              ...o,
              hidden: o.title.toLowerCase().indexOf(newInputValue.toLowerCase()) === -1
            }
        })
        setOptions(newOptions)
        setInputValue(newInputValue || '') // Set searched value in component state
      }}
      isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id} // Logic to determine if an options is selected
      getOptionDisabled={(option) => !!option.disabled}
      filterOptions={(options) => options.filter((option) => !option.hidden)}
      options={options} // Options in the dropdown
      {...filterOptions} // Use FE or BE search & filter
      id={field.name}
      fullWidth
      disabled={disabled}
      disableClearable={disableClearable}
      loading={isFetching} // Show loading indicator in the dropdown
      getOptionLabel={(option: IValue) => {
        let title = ''
        if (option?.parents) {
          title = generateTitle(title, option) // For nested option
        } else {
          title = option?.title || '' // For classic options
        }

        return title
      }}
      renderInput={(params) => (
        <TextField
          sx={{
            background: theme.palette.background.default,
            zIndex: 1,
            position: 'relative',
            // paddingBottom: '1.2rem',
            '& .MuiInputBase-root': { borderRadius: ELEMENT_RADIUS_MID, padding: '0.75rem !important' },
            '& label': { top: '2px !important' },
            '& input': { padding: '0 !important', textTransform: 'capitalize', maxWidth: `calc(100% - 50px)` },
          }}
          // variant='standard' // Field design
          {...params} // Autocomplete Render params
          {...fieldProps} // Set filed name and label
          error={Boolean(error) && touched} // Toggle helper text used for error messages
          helperText={renderHelperText()} // Error messages component
          autoComplete='off' // Stop browser suggestions based on previous searches
          InputProps={{
            // Handle loading and error state
            ...params.InputProps,
            readOnly: true,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color='inherit' size={20} /> : null}{' '}
                {/* Show loading indicator in text field */}
                {fetchError ? <Chip label='Error' color='error' size='small' /> : null}{' '}
                {/* Show error indicator in text field */}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option, state) => (
        <DropItem
          key={props['data-option-index']}
          state={state}
          props={props}
          multiple={multiple}
          option={option}
          optionsCount={options.length - 1}
          optionIndex={props['data-option-index']}
          hasMore={data?.hasMore}
          hasHint={hasHint}
          setPage={setPage}
          renderChildren={renderChildren}
        />
      )}
    />
    
    {hint && <div style={{
      position: 'absolute',
      zIndex: '2',
      top: '-10px',
      right: '-10px'
    }}><InfoTooltip hint={hint}></InfoTooltip></div>}
    </div>
  )
}

export default SelectField

// Custom component to handle scroll to top on adding new options (infinity scroll)
type NullableUlElement = HTMLUListElement | null
interface ListBoxProps extends React.HTMLAttributes<HTMLUListElement> {}

const DropDownList = forwardRef(function ListBoxBase(
  props: ListBoxProps,
  ref: ForwardedRef<HTMLUListElement>,
) {
  const { children, ...rest } = props

  const innerRef = useRef<HTMLUListElement>(null)

  useImperativeHandle<NullableUlElement, NullableUlElement>(ref, () => innerRef.current)

  return (
    // role='list-box' is mandatory to prevent auto scroll to top on options change
    <ul {...rest} ref={innerRef} role='list-box'>
      {children}
    </ul>
  )
})

// Custom Drop item component
type Props = HTMLAttributes<HTMLLIElement>
interface IDropDownItemProps {
  state: any
  props: Props
  option: IValue
  optionsCount: number
  optionIndex: number
  hasMore: boolean
  multiple: boolean
  setPage: Dispatch<React.SetStateAction<number>>
  renderChildren: boolean
  hasHint: boolean
}

const DropItem: FC<IDropDownItemProps> = ({
  state,
  props,
  option,
  optionsCount,
  optionIndex,
  hasMore,
  multiple,
  setPage,
  renderChildren,
  hasHint,
}) => {
  // Dropdown list element abstraction - (DRY compliance)
  const renderListItem = (props: Props, option: IValue, title = '', type: 'single' | 'multi' = 'single', hint = null) => (
    <li
      {...props}
      key={optionIndex}
      style={{
        paddingLeft: `${option?.parents && option?.parents.length ? option.parents.length * 1.3 + 1 : 1}rem`,
      }}
    >
      {type === 'multi' && (
        <Checkbox
          icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
          checkedIcon={<CheckBoxIcon fontSize='small' />}
          style={{ marginRight: 8 }}
          checked={state.selected}
        />
      )}
      {title}

      { hint && <InfoTooltip hint={hint}></InfoTooltip>}


    </li>
  )

  // Filter option children if necessary
  const renderSingleSelectItem = (props: Props, option: IValue) =>
    renderChildren
      ? renderListItem(props, option, option.title, 'single', hasHint ? option.hint : null)
      : option?.parents?.length
      ? null
      : renderListItem(props, option, option.title, 'single', hasHint ? option.hint : null)

  const renderMultiSelectItem = (state: any, props: Props, option: IValue) =>
    renderChildren
      ? renderListItem(props, option, option.title, 'multi', hasHint ? option.hint : null)
      : option?.parents?.length
      ? null
      : renderListItem(props, option, option.title, 'multi', hasHint ? option.hint : null)

  // Attach last element observer for infinity scroll
  return hasMore && optionsCount === optionIndex ? (
    <Waypoint key={optionIndex} onEnter={() => setPage((prevPage) => prevPage + 1)}>
      {multiple ? renderMultiSelectItem(state, props, option) : renderSingleSelectItem(props, option)}
    </Waypoint>
  ) : (
    <>{multiple ? renderMultiSelectItem(state, props, option) : renderSingleSelectItem(props, option)}</>
  )
}
